import { Card, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { login as apiLogin } from '../../services/authService';
import { FaLock } from 'react-icons/fa';

export default function Login() {
  const navigate = useNavigate();
  const { login, checkAdminAccess } = useAuth();
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setIsLoading(true);

    const formData = new FormData(event.currentTarget);
    const email = (formData.get('email') as string)?.trim().toLowerCase();
    const password = formData.get('password') as string;

    if (!email || !password) {
      setError('Email and password are required');
      setIsLoading(false);
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      setIsLoading(false);
      return;
    }

    // Optional: client-side admin whitelist check
    if (!checkAdminAccess(email)) {
      setError('This email is not authorized to access the admin dashboard');
      setIsLoading(false);
      return;
    }

    try {
      // Call the real login service
      const response = await apiLogin(email, password);
      
      if (response.success) {
        if (response.verified === false) {
          // Handle unverified account - redirect to OTP (if available) or show message
          setError('Email verification required. An OTP has been sent to your email.');
          // TODO: navigate('/verify-otp', { state: { email } });
          return;
        }

        if (response.data?.token && response.data?.user) {
          // Store auth data in context and local storage via the context's login method
          await login(response.data.user, response.data.token);
          navigate('/', { replace: true });
        } else {
          setError('Invalid server response. Token or user data missing.');
        }
      } else {
        setError(response.message || 'Failed to authenticate. Please try again.');
      }
    } catch (err: any) {
      console.error('Login error:', err);
      // Handle axios/network errors
      const errorMessage = err.response?.data?.message || err.message || 'Authentication failed. Please check your credentials and try again.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="d-flex align-items-center justify-content-center vh-100 bg-light">
      <Card style={{ width: 420 }} className="shadow-sm">
        <Card.Body className="p-4">
          <div className="text-center mb-4">
            <FaLock size={32} className="text-primary mb-3" />
            <Card.Title as="h3">Admin Login</Card.Title>
            <p className="text-muted mb-0">Enter your credentials to continue</p>
          </div>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Email Address</Form.Label>
              <Form.Control
                name="email"
                type="email"
                placeholder="admin@teo-eg.com"
                autoComplete="email"
                required
                disabled={isLoading}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Password</Form.Label>
              <Form.Control
                name="password"
                type="password"
                placeholder="••••••••"
                autoComplete="current-password"
                required
                disabled={isLoading}
              />
            </Form.Group>
            {error && (
              <Alert variant={error.includes('verification required') ? 'info' : 'danger'} className="mb-3">
                {error}
              </Alert>
            )}
            <Button type="submit" className="w-100" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Authenticating...
                </>
              ) : (
                'Login'
              )}
            </Button>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
}

